import * as formik from 'formik';
import * as fabric from 'office-ui-fabric-react';
import { IDropdownOption } from 'office-ui-fabric-react';
import { FC, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import * as api from '../../../../../api/generated';
import { KendoLoading } from '../../../../../components';
import { IWindowComponentProps } from '../../../../../components/windows/WindowContext';

interface LocationConfigTerminalServiceCreateWindowProps {
    fetchData: () => Promise<void>;
    terminalId: number;
    floor: number;
    organizationInformationId: number;
}

interface ITerminalServiceForm {
    serviceId: number | null;
    terminalId: number;
    groupId: number | null;
    orderLevel: number;
    groupServiceOrderLevel: number;
    floor: number;
}

interface IGroupName {
    id: number;
    name: string | null;
}

const validationSchema = Yup.object<ITerminalServiceForm>().shape({
    serviceId: Yup.number().required('Šis laukas yra privalomas').nullable(),
    orderLevel: Yup.number().required('Šis laukas yra privalomas').moreThan(-1, 'Skaičius turi būti teigiamas'),
    floor: Yup.number().required('Šis laukas yra privalomas').moreThan(0, 'Skaičius turi būti daugiau nei 0')
});

const LocationConfigTerminalServiceCreateWindow: FC<IWindowComponentProps<LocationConfigTerminalServiceCreateWindowProps>> = (props) => {
    const {
        componentProps: { fetchData, terminalId, floor, organizationInformationId },
        onClose
    } = props;

    const [terminalServiceForm, setTerminalServiceForm] = useState<ITerminalServiceForm | null>(null);
    const [allServices, setAllServices] = useState<IDropdownOption[]>([]);
    const [allGroups, setAllGroups] = useState<IGroupName[] | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            const [servicesResp, groupsResp] = await Promise.all([
                api.service.getAllServices(parseInt(organizationInformationId.toString())),
                api.groups.getGroupsNames(parseInt(organizationInformationId.toString()))
            ]);

            setAllServices(mapServicesOptions(servicesResp));
            setAllGroups(groupsResp);

            setTerminalServiceForm({
                serviceId: null,
                terminalId: terminalId,
                orderLevel: 0,
                groupId: null,
                floor: floor,
                groupServiceOrderLevel: 0
            });
        };
        fetchData();
    }, []);

    const handleSubmit = async (values: ITerminalServiceForm) => {
        setIsLoading(true);
        try {
            values.serviceId &&
                values.terminalId &&
                (await api.terminalServices.createTerminalService({
                    serviceId: values.serviceId,
                    terminalId: terminalId,
                    groupId: values.groupId,
                    floor: values.floor,
                    orderLevel: values.orderLevel,
                    groupServiceOrderLevel: values.groupServiceOrderLevel
                }));

            toast.success('Paslauga terminalui priskirta sėkmingai');
        } catch (ex) {
            toast.error(ex.error[0]);
        } finally {
            setIsLoading(false);
            await fetchData();
            onClose();
        }
    };

    const mapServicesOptions = (allServices): fabric.IDropdownOption[] => {
        var options: fabric.IDropdownOption[] = [];
        allServices.map((x) => {
            options.push({ key: x.id, text: x.text });
        });
        return options;
    };

    const mapGroupsToOptions = (groups: IGroupName[]): fabric.IDropdownOption[] => {
        var options: fabric.IDropdownOption[] = [];
        options.push({
            key: 'null',
            text: '—'
        });
        groups.map((x) => {
            options.push({ key: x.id, text: x.name ? x.name : '' });
        });
        return options;
    };

    return (
        <div>
            {(!terminalServiceForm || !allServices || !allGroups || isLoading) && <KendoLoading />}
            {terminalServiceForm && allServices && allGroups && (
                <formik.Formik validationSchema={validationSchema} initialValues={{ ...terminalServiceForm }} onSubmit={handleSubmit}>
                    {(formikProps) => (
                        <formik.Form>
                            <div className="ms-Grid-row">
                                <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                    <formik.Field name="serviceId">
                                        {({ field, form }: formik.FieldProps<number | null>) => (
                                            <fabric.Dropdown
                                                {...field}
                                                options={allServices}
                                                label="Paslaugos"
                                                placeholder="Pasirinkite paslaugą"
                                                onChange={({}, value: any) => form.setFieldValue(field.name, value.key)}
                                                defaultSelectedKey={field.value}
                                            />
                                        )}
                                    </formik.Field>
                                </div>
                                <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                    <formik.Field name="groupId">
                                        {({ field, form }: formik.FieldProps<number | null>) => (
                                            <fabric.Dropdown
                                                {...field}
                                                label="Grupė"
                                                defaultSelectedKey={field.value}
                                                placeholder="Pasirinkite grupę"
                                                options={mapGroupsToOptions(allGroups)}
                                                onChange={({}, value: any) =>
                                                    value.key === 'null'
                                                        ? form.setFieldValue(field.name, null)
                                                        : form.setFieldValue(field.name, value.key)
                                                }
                                                errorMessage={
                                                    (formikProps.status && formikProps.status.groupId) || (form.errors.groupId && form.errors.groupId)
                                                }
                                            />
                                        )}
                                    </formik.Field>
                                </div>
                            </div>
                            <div className="ms-Grid-row">
                                <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                    <formik.Field name="orderLevel">
                                        {({ field, form }: formik.FieldProps<string>) => (
                                            <fabric.TextField
                                                {...field}
                                                label={`Paslaugos išdėstymo eiliškumas ${formikProps.values.groupId ? 'grupėje' : ''}`}
                                                type="number"
                                                maxLength={3}
                                                errorMessage={
                                                    (formikProps.status && formikProps.status.orderLevel) ||
                                                    (form.errors.orderLevel && form.errors.orderLevel)
                                                }
                                            />
                                        )}
                                    </formik.Field>
                                </div>
                            </div>
                            {formikProps.values.groupId ? (
                                <div className="ms-Grid-row">
                                    <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                        <formik.Field name="groupServiceOrderLevel">
                                            {({ field, form }: formik.FieldProps<string>) => (
                                                <fabric.TextField
                                                    {...field}
                                                    label="Grupės išdėstymo sąraše eiliškumas"
                                                    type="number"
                                                    maxLength={3}
                                                    errorMessage={
                                                        (formikProps.status && formikProps.status.groupServiceOrderLevel) ||
                                                        (form.errors.groupServiceOrderLevel && form.errors.groupServiceOrderLevel)
                                                    }
                                                />
                                            )}
                                        </formik.Field>
                                    </div>
                                </div>
                            ) : null}
                            <div className="ms-Grid-row">
                                <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                    <formik.Field name="floor">
                                        {({ field, form }: formik.FieldProps<string>) => (
                                            <fabric.TextField
                                                {...field}
                                                label="Aukštas"
                                                type="number"
                                                maxLength={2}
                                                errorMessage={
                                                    (formikProps.status && formikProps.status.floor) || (form.errors.floor && form.errors.floor)
                                                }
                                            />
                                        )}
                                    </formik.Field>
                                </div>
                            </div>
                            <div style={{ textAlign: 'center', marginTop: '30px' }}>
                                <fabric.PrimaryButton
                                    className="ms-slideRightIn40 ms-textAlignRight"
                                    text="Išsaugoti"
                                    disabled={formikProps.isSubmitting || !formikProps.isValid || !formikProps.dirty}
                                    type="submit"
                                />
                            </div>
                        </formik.Form>
                    )}
                </formik.Formik>
            )}
        </div>
    );
};

export default LocationConfigTerminalServiceCreateWindow;
